import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { HttpErrorResponse } from '@angular/common/http'
import { CallerService } from '../services/caller.service'
import { SaleType } from '../ngrx-store/app.state'
import { SeoService } from '../services/seo.service'
import { SaleActionsService } from '../services/sale-actions.service'
import { loadSavedSales } from '../ngrx-store/actions/corp-member.actions'
import { selectSavedSaleIds } from '../ngrx-store/selectors/corp.selectors'
import { categoryLabel, conditionLabel, leavingLabel, saleLocation } from '../utils/sale.util'
import { RETURN_URL_QUERY_PARAM } from '../utils/return-url.util'

/** One item for sale (/s/:id) - a shared link, so logged out too. Laid out like the house page. */
@Component({
  selector: 'app-public-sale',
  templateUrl: './public-sale.component.html',
  styleUrls: ['./public-sale.component.css'],
})
export class PublicSaleComponent implements OnInit, OnDestroy {
  sale?: SaleType
  isLoading = true
  notFound = false
  private destroy$ = new Subject<void>()
  loadError = false
  isSaving = false
  isUpdatingSold = false
  private savedIds = new Set<number>()

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public callerService: CallerService,
    private seo: SeoService,
    public saleActions: SaleActionsService,
  ) {}

  ngOnInit() {
    this.loadSale()
    if (this.callerService.isLoggedIn) {
      this.callerService.store.dispatch(loadSavedSales())
      this.callerService.store
        .select(selectSavedSaleIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe((ids) => (this.savedIds = ids))
    }
  }

  loadSale() {
    this.isLoading = true
    this.notFound = false
    this.loadError = false

    const id = this.route.snapshot.paramMap.get('id')
    this.callerService.getPublicSale(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.body?.data) {
          this.sale = res.body.data
          this.updateSeo(res.body.data)
        } else {
          this.showNotFound()
        }
        this.isLoading = false
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.showNotFound()
        } else {
          this.loadError = true
        }
        this.isLoading = false
      },
    })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private showNotFound() {
    this.notFound = true
    this.seo.setPage({ title: 'Not found', path: this.router.url, noindex: true })
  }

  private updateSeo(sale: SaleType) {
    const state = sale.CorpMember?.service_state
    this.seo.setPage({
      title: `${sale.item_name} for sale – ₦${sale._price}`,
      description: [sale.text, `Sold by a corps member${state ? ` in ${state}` : ''} on corpers.ng.`]
        .filter(Boolean)
        .join(' · '),
      image: sale.Media?.[0]?.url,
      path: this.router.url,
    })
  }

  get sellerDisplayName(): string {
    return this.sale?.CorpMember?.nickname ?? this.sale?.CorpMember?.first_name ?? 'the seller'
  }

  /** "Aba South, Abia". */
  get location(): string {
    return saleLocation(this.sale?.CorpMember?._location)
  }

  /** Logged out: sign up, then come back to this item. */
  get signupQueryParams(): Record<string, string> {
    return { [RETURN_URL_QUERY_PARAM]: this.router.url.split(/[?#]/)[0] }
  }

  get sellerInitial(): string {
    return (this.sale?.CorpMember?.first_name?.[0] ?? 'S').toUpperCase()
  }

  get sellerProfileLink(): string[] {
    const slug = this.sale?.CorpMember?.slug_id ?? this.sale?.CorpMember?.created_at
    return slug ? ['/p', slug] : ['/home']
  }

  get saved(): boolean {
    return !!this.sale && this.savedIds.has(this.sale.id)
  }

  get isOwn(): boolean {
    return this.saleActions.isOwnSale(this.sale)
  }

  get condition(): string | undefined {
    return conditionLabel(this.sale?.condition)
  }

  get category(): string | undefined {
    return categoryLabel(this.sale?.category)
  }

  get leaving(): string | undefined {
    return leavingLabel(this.sale?.CorpMember?.service_year_end_date)
  }

  toggleSave() {
    if (!this.sale) return
    this.isSaving = true
    this.saleActions.toggleSave(this.sale, this.saved).subscribe(() => (this.isSaving = false))
  }

  toggleSold() {
    const sale = this.sale
    if (!sale) return
    this.isUpdatingSold = true
    this.saleActions.setSold(sale, !sale.sold_at).subscribe((soldAt) => {
      this.isUpdatingSold = false
      if (soldAt !== undefined) this.sale = { ...sale, sold_at: soldAt }
    })
  }
}
